import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * DATO QUE SE REFRESCA SOLO, SIN QUE NADIE APRIETE F5.
 *
 * El caso que lo destapó: se publicaba un anuncio interno y no lo veía nadie. La barra del avión
 * consultaba UNA vez al montar, y como la app es una SPA que nadie recarga, la gente pasaba el día
 * entero con la pestaña abierta y el anuncio no aparecía nunca. Lo mismo aplica a cualquier aviso
 * app-wide: si hace falta un refresh para enterarse, en la práctica no se entera nadie.
 *
 * Tres disparadores, porque cada uno cubre un agujero del otro:
 *
 *  1. INTERVALO — el piso. Aunque la persona no toque nada, el aviso entra solo.
 *  2. VOLVER A LA PESTAÑA — el caso real más común: se van a Gmail, vuelven, y quieren verlo YA,
 *     no dentro de un minuto. Además, mientras la pestaña está oculta NO se consulta: sin esto,
 *     veinte pestañas de fondo martillan el backend todo el día para nada.
 *  3. CAMBIAR DE PANTALLA — lo que pidió el dueño ("que la próxima vez que le den un clic
 *     revise"). En una SPA navegar es la señal de "esta persona está trabajando ahora".
 *
 * `fetcher` se guarda en una ref: así se puede pasar una función inline sin que cada render
 * reinicie el intervalo (el error clásico que convierte un poll de 60s en uno de cada render).
 */
export function useLive<T>(
  fetcher: () => Promise<T>,
  opts: { everyMs?: number; enabled?: boolean } = {},
): { data: T | null; refresh: () => void } {
  const { everyMs = 60_000, enabled = true } = opts;
  const [data, setData] = useState<T | null>(null);
  const fRef = useRef(fetcher);
  fRef.current = fetcher;
  const vivo = useRef(true);
  const { pathname } = useLocation();

  const refresh = useCallback(() => {
    if (!enabled) return;
    fRef.current().then((d) => { if (vivo.current) setData(d); }).catch(() => { /* un aviso no rompe la pantalla */ });
  }, [enabled]);

  useEffect(() => {
    vivo.current = true;
    return () => { vivo.current = false; };
  }, []);

  // (1) intervalo + (2) volver a la pestaña
  useEffect(() => {
    if (!enabled) return undefined;
    refresh();
    const id = window.setInterval(() => { if (!document.hidden) refresh(); }, everyMs);
    const alVolver = () => { if (!document.hidden) refresh(); };
    document.addEventListener('visibilitychange', alVolver);
    window.addEventListener('focus', alVolver);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', alVolver);
      window.removeEventListener('focus', alVolver);
    };
  }, [refresh, everyMs, enabled]);

  // (3) cambiar de pantalla
  useEffect(() => { refresh(); }, [pathname, refresh]);

  return { data, refresh };
}
